import React, { useEffect } from 'react';
import { flushSync } from 'react-dom';
import GroupRenderer from './GroupRenderer';
import type { Group, GroupChild, Guest, SeatChild, SeatNode, SeatToGuestMap, TableChild } from '../types';
import { applySeatStates } from '../utils/updateSeatStates';
import '../styles/RoomSVG.css';

interface RoomSVGProps {
  groups: Group[];
  handleTableClick?: (table: TableChild) => void;
  handleSeatClick?: (seat: SeatNode | SeatChild) => void;
  seatToGuestMap?: SeatToGuestMap;
  guestCodes?: Guest[];
  parseARGBToRGBA?: (argb: string) => string;
  zoomToTableId?: string | null;
}

const RoomSVG: React.FC<RoomSVGProps> = ({
  groups,
  handleTableClick,
  handleSeatClick,
  seatToGuestMap,
  guestCodes,
  parseARGBToRGBA,
  // zoomToTableId is handled by SeatMap → handleTableClickZoom directly
}) => {
  // --- Panning state ---
  const [viewBox, setViewBox] = React.useState<string>("");
  const [isPanning, setIsPanning] = React.useState(false);
  const [startPoint, setStartPoint] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const svgRef = React.useRef<SVGSVGElement | null>(null);

  // Store the initial bounding box for zoom-out
  const [initialViewBox, setInitialViewBox] = React.useState<string>("");

  // Calculate bounding box for all groups
  function calculateBoundingBox(groups: Group[]) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    groups.forEach(group => {
      if (group.left !== undefined && group.top !== undefined) {
        minX = Math.min(minX, group.left);
        minY = Math.min(minY, group.top);
        maxX = Math.max(maxX, group.left + (group.width || 0));
        maxY = Math.max(maxY, group.top + (group.height || 0));
      }
    });
    if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
      return { x: 0, y: 0, width: 1000, height: 1000 };
    }
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  // Set initial viewBox only on mount
  React.useEffect(() => {
    if (viewBox === "") {
      const boundingBox = calculateBoundingBox(groups);
      if (
        Number.isFinite(boundingBox.x) &&
        Number.isFinite(boundingBox.y) &&
        Number.isFinite(boundingBox.width) &&
        Number.isFinite(boundingBox.height)
      ) {
        const vb = `${boundingBox.x} ${boundingBox.y} ${boundingBox.width} ${boundingBox.height}`;
        setViewBox(vb);
        setInitialViewBox(vb);
      } else {
        // Fallback to a safe default
        setViewBox("0 0 1000 1000");
        setInitialViewBox("0 0 1000 1000");
      }
    }
  }, [groups]); // Also rerun if groups change

  // Defensive: never allow empty viewBox string
  const safeViewBox =
    viewBox && viewBox.split(' ').length === 4 && viewBox.split(' ').every(v => v !== "" && !isNaN(Number(v)))
      ? viewBox
      : "0 0 1000 1000";

  // Keep a ref to the latest viewBox for animation
  const viewBoxRef = React.useRef(viewBox);
  React.useEffect(() => {
    viewBoxRef.current = viewBox;
  }, [viewBox]);

  // --- Run the diff-based seat-state painter -------------------------
  // ``GroupRenderer`` is memoized and does not re-render when seat
  // assignments change. Instead, the painter mutates classes directly on
  // the registered <circle>/<g> elements, in this single effect.
  React.useEffect(() => {
    applySeatStates(seatToGuestMap, guestCodes);
  }, [seatToGuestMap, guestCodes, groups]);

  // --- Animated zoom (imperative viewBox via rAF) --------------------
  //
  // We mutate the SVG ``viewBox`` attribute directly on each animation
  // frame via the ``svgRef``. This bypasses React entirely during the
  // tween, so the now-declarative SVG tree is not reconciled per frame.
  // Only the final value is committed to React state, which triggers a
  // single re-render at the end. ``prefers-reduced-motion`` skips the
  // tween and just commits the target value.
  const animationFrameRef = React.useRef<number | null>(null);

  const cancelAnimation = React.useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const animateZoomTo = React.useCallback(
    (targetX: number, targetY: number, targetW: number, targetH: number, duration = 500) => {
      const target = `${targetX} ${targetY} ${targetW} ${targetH}`;

      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        viewBoxRef.current = target;
        setViewBox(target);
        return;
      }

      cancelAnimation();

      const parts = viewBoxRef.current.split(' ').map(Number);
      if (parts.length !== 4 || parts.some(n => !Number.isFinite(n))) {
        viewBoxRef.current = target;
        setViewBox(target);
        return;
      }
      const [curX, curY, curW, curH] = parts;
      if (curX === targetX && curY === targetY && curW === targetW && curH === targetH) {
        return;
      }

      // ease-in-out cubic
      const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

      const start = performance.now();
      const step = (now: number) => {
        const progress = Math.min(1, (now - start) / duration);
        const e = ease(progress);
        const x = curX + (targetX - curX) * e;
        const y = curY + (targetY - curY) * e;
        const w = curW + (targetW - curW) * e;
        const h = curH + (targetH - curH) * e;
        const vb = `${x} ${y} ${w} ${h}`;
        // Bypass React entirely during the tween: set the DOM attribute directly.
        // setViewBox (React state) is only called once at the very end so that
        // panning/wheel logic which reads from `viewBox` state stays in sync.
        // Any React re-renders that happen during the tween (e.g. from the
        // handleTableClick state update that was already flushed before we
        // started) will not conflict because we do not touch React state here.
        svgRef.current?.setAttribute('viewBox', vb);
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(step);
        } else {
          setViewBox(vb); // sync React state once, at end
          animationFrameRef.current = null;
        }
      };
      animationFrameRef.current = requestAnimationFrame(step);
    },
    [cancelAnimation]
  );

  React.useEffect(() => () => cancelAnimation(), [cancelAnimation]);

  // Intercept table clicks to zoom
  const handleTableClickZoom = (table: TableChild) => {
    // Find the table in the group tree to compute its absolute position.
    let foundTable: TableChild | null = null;
    let groupOffset = { left: 0, top: 0 };

    for (const group of groups) {
      if (group.children) {
        const t = group.children.find(
          (child: GroupChild) => child.type === 'table' && (child as TableChild).guid === table.guid
        ) as TableChild | undefined;
        if (t) {
          foundTable = t;
          groupOffset = { left: group.left || 0, top: group.top || 0 };
          break;
        }
      }
    }

    // Flush handleTableClick first — SeatMap.setZoomToTableId and any
    // MainLayout state updates inside it are committed synchronously here,
    // so there are no pending React re-renders left to overwrite our frames.
    flushSync(() => { if (handleTableClick) handleTableClick(table); });

    if (foundTable) {
      let absX = (foundTable.left ?? 0) + groupOffset.left;
      let absY = (foundTable.top ?? 0) + groupOffset.top;
      let w = foundTable.width ?? 0;
      let h = foundTable.height ?? 0;
      const pad = 0.50 * Math.max(w, h);
      animateZoomTo(absX - pad, absY - pad, w + 2 * pad, h + 2 * pad, 500);
    }
  };

  // --- Panning logic ---
  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button !== 0) return; // Only left mouse button
    setIsPanning(true);
    setStartPoint({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!isPanning || !svgRef.current) return;
    const [x, y, width, height] = viewBox.split(' ').map(Number);
    // Get room bounding box
    const boundingBox = calculateBoundingBox(groups);
    // If the current viewBox fully contains the room, prevent panning
    const roomFullyVisible =
      x <= boundingBox.x &&
      y <= boundingBox.y &&
      x + width >= boundingBox.x + boundingBox.width &&
      y + height >= boundingBox.y + boundingBox.height;
    if (roomFullyVisible) {
      return;
    }
    // Otherwise, allow panning
    const dx = (e.clientX - startPoint.x) * (width / svgRef.current.clientWidth);
    const dy = (e.clientY - startPoint.y) * (height / svgRef.current.clientHeight);
    setViewBox(`${x - dx} ${y - dy} ${width} ${height}`);
    setStartPoint({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // --- Zoom with mouse wheel (ctrl+wheel or trackpad pinch) ---
  // Set min scale to 1/3 (zoomed in), max scale to 1 (fit-to-room, cannot zoom out further)
  const MIN_SCALE = 1 / 6; // allow up to 3x zoom in
  const MAX_SCALE = 1; // fit-to-room (cannot zoom out further)
  // Store initial width/height for scale calculations
  const [initialDims, setInitialDims] = React.useState<{ width: number; height: number }>({ width: 1000, height: 1000 });
  useEffect(() => {
    if (initialViewBox) {
      const [, , w, h] = initialViewBox.split(' ').map(Number);
      setInitialDims({ width: w, height: h });
    }
  }, [initialViewBox]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const handleNativeWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
        const [x, y, width, height] = viewBoxRef.current.split(' ').map(Number);
        const rect = svg.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const clientWidth = rect.width;
        const clientHeight = rect.height;
        const zoomFactor = e.deltaY > 0 ? 1.15 : 0.85;
        // Clamp so user cannot zoom out further than fit, or in further than 3x
        let newWidth = width * zoomFactor;
        let newHeight = height * zoomFactor;
        const minWidth = initialDims.width * MIN_SCALE;
        const maxWidth = initialDims.width * MAX_SCALE;
        const minHeight = initialDims.height * MIN_SCALE;
        const maxHeight = initialDims.height * MAX_SCALE;
        newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
        newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
        const zoomX = x + (mouseX / clientWidth) * width;
        const zoomY = y + (mouseY / clientHeight) * height;
        const newX = zoomX - (mouseX / clientWidth) * newWidth;
        const newY = zoomY - (mouseY / clientHeight) * newHeight;
        setViewBox(`${newX} ${newY} ${newWidth} ${newHeight}`);
      }
    };
    svg.addEventListener('wheel', handleNativeWheel, { passive: false });
    return () => {
      svg.removeEventListener('wheel', handleNativeWheel);
    };
  }, [svgRef, initialDims]);

  // Zoom-out handler (animated via CSS transform; reduced-motion safe)
  const handleZoomOut = () => {
    if (!initialViewBox) return;
    const [targetX, targetY, targetW, targetH] = initialViewBox.split(' ').map(Number);
    animateZoomTo(targetX, targetY, targetW, targetH, 300);
  };

  // Use initialViewBox in the component (for zoom-out and button state)
  // Remove unused variable warning by referencing it in the return
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <button
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 10,
          padding: '6px 14px',
          fontSize: 16,
          borderRadius: 6,
          border: '1px solid #888',
          background: '#fff',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
        onClick={handleZoomOut}
        disabled={viewBox === initialViewBox}
        title="Gesamtansicht (Zoom zurücksetzen)"
        aria-label="Gesamtansicht (Zoom zurücksetzen)"
      >
        ⤢ Gesamtansicht
      </button>
      <svg
        ref={svgRef}
        className="room-svg"
        viewBox={safeViewBox}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        // onWheel removed, handled natively
        style={{
          cursor: isPanning ? 'grabbing' : 'grab',
          width: '100%',
          height: '100%',
          background: '#ffffff',
        }}
      >
        {groups.map((group, index) => (
          <GroupRenderer
            key={index}
            group={group}
            handleTableClick={handleTableClickZoom}
            handleSeatClick={handleSeatClick}
            parseARGBToRGBA={parseARGBToRGBA}
          />
        ))}
      </svg>
    </div>
  );
};

export default RoomSVG;